import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  BenefitDesignQuotesList,
  ShopInsuranceQuotesList,
  JointPolicyComingSoon,
  type BenefitDesignQuoteGroup,
  type PaymentOption,
  type ShopInsuranceMode,
  type TraditionalLTCQuoteWithROI,
  type QuoteWithROI,
} from '@/features/shop-insurance';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { cn } from '@client/lib';

interface ShopInsuranceTabContentProps {
  underwritingFetchStarted: boolean;
  underwritingFetchComplete: boolean;
  filteredAdjustedQuotes: QuoteWithROI[];
  filteredTraditionalLTCQuotesWithEligibility: TraditionalLTCQuoteWithROI[];
  underwritingResults: Map<string, 'eligible' | 'ineligible' | 'unsure' | null>;
  expandedIndices: number[];
  selectedTab: ShopInsuranceTab;
  clientPartner: any;
  premiumValue: number;
  paymentOption: PaymentOption;
  advisorId: string;
  isQuoteSelected: (quote: any) => { isSelected: boolean; fundingSourceId: string | null };
  handleCardToggle: (index: number) => void;
  handleViewUnderwritingDetails: (quoteId: string, productName: string) => void;
  onClose: () => void;
  knownLimitationError?: QuoteError | null;
  hasSameGenderPartner: boolean;
  mode?: ShopInsuranceMode;
  benefitDesignQuoteGroups?: BenefitDesignQuoteGroup[];
  stateOfResidence?: string;
}

export function ShopInsuranceTabContent({
  underwritingFetchStarted,
  underwritingFetchComplete,
  filteredAdjustedQuotes,
  filteredTraditionalLTCQuotesWithEligibility,
  underwritingResults,
  expandedIndices,
  selectedTab,
  clientPartner,
  premiumValue,
  paymentOption,
  advisorId,
  isQuoteSelected,
  handleCardToggle,
  handleViewUnderwritingDetails,
  onClose,
  knownLimitationError,
  hasSameGenderPartner,
  mode = 'premium-optimization',
  benefitDesignQuoteGroups = [],
  stateOfResidence,
}: ShopInsuranceTabContentProps) {
  const { flags } = useFeatureFlags();

  if (selectedTab === 'joint' && hasSameGenderPartner) {
    return <JointPolicyComingSoon />;
  }

  if (underwritingFetchStarted && !underwritingFetchComplete) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 py-12">
        <LoadingSpinner testId="shop-insurance-modal-loading-spinner" />
        <p className="text-foreground text-sm">Checking eligibility for available products...</p>
      </div>
    );
  }

  const traditionalLTCQuotes =
    flags.nycLifeDemo || mode === 'benefit-design'
      ? []
      : filteredTraditionalLTCQuotesWithEligibility;
  const sharedListProps = {
    filteredTraditionalLTCQuotesWithEligibility: traditionalLTCQuotes,
    underwritingResults,
    expandedIndices,
    selectedTab,
    clientPartner,
    paymentOption,
    advisorId,
    isQuoteSelected,
    handleCardToggle,
    handleViewUnderwritingDetails,
    onClose,
    knownLimitationError,
  };

  return (
    <div
      data-testid="shop-insurance-quotes-list"
      className="flex w-full flex-col"
    >
      {mode === 'benefit-design' ? (
        <BenefitDesignQuotesList
          quoteGroups={benefitDesignQuoteGroups}
          filteredTraditionalLTCQuotesWithEligibility={traditionalLTCQuotes}
          underwritingResults={underwritingResults}
          selectedTab={selectedTab}
          clientPartner={clientPartner}
          paymentOption={paymentOption}
          advisorId={advisorId}
          isQuoteSelected={isQuoteSelected}
          handleViewUnderwritingDetails={handleViewUnderwritingDetails}
          onClose={onClose}
          knownLimitationError={knownLimitationError}
        />
      ) : (
        <ShopInsuranceQuotesList
          filteredAdjustedQuotes={filteredAdjustedQuotes}
          premiumValue={premiumValue}
          stateOfResidence={stateOfResidence}
          {...sharedListProps}
        />
      )}
    </div>
  );
}
